import { useState } from 'react'
import { BAD, GOOD, GRID, INK, INK_MID } from './chartColors.js'
import { round } from './scales.js'

interface DivergingBarsProps {
  periods: readonly string[]
  /** Drawn above the line (the good direction). */
  up: readonly (number | null)[]
  /** Drawn below the line, as positive counts. */
  down: readonly (number | null)[]
  upName: string
  downName: string
  periodLabel: (period: string) => string
  title: string
  height?: number
}

const WIDTH = 600
const PAD = { top: 16, right: 12, bottom: 24, left: 40 }

/** One column per month, the good count above a centre line and the bad below, so the balance reads at a glance. */
export function DivergingBars({ periods, up, down, upName, downName, periodLabel, title, height = 240 }: DivergingBarsProps) {
  const [hover, setHover] = useState<number | null>(null)
  const plotWidth = WIDTH - PAD.left - PAD.right
  const half = (height - PAD.top - PAD.bottom) / 2
  const centre = PAD.top + half
  const slot = plotWidth / Math.max(1, periods.length)
  const barWidth = Math.min(40, slot * 0.7)
  const max = Math.max(1, ...up.map((value) => value ?? 0), ...down.map((value) => value ?? 0))
  const scale = (value: number): number => (value / max) * (half - 4)
  return (
    <svg role="img" aria-label={title} viewBox={`0 0 ${WIDTH} ${height}`} className="block w-full" onPointerLeave={() => setHover(null)}>
      <desc>
        {title}. Over the span: {up.reduce<number>((sum, value) => sum + (value ?? 0), 0)} {upName.toLowerCase()}, {down.reduce<number>((sum, value) => sum + (value ?? 0), 0)} {downName.toLowerCase()}.
      </desc>
      <line x1={PAD.left} y1={round(PAD.top)} x2={WIDTH - PAD.right} y2={round(PAD.top)} stroke={GRID} />
      <line x1={PAD.left} y1={round(centre)} x2={WIDTH - PAD.right} y2={round(centre)} stroke={INK_MID} />
      <line x1={PAD.left} y1={round(centre + half)} x2={WIDTH - PAD.right} y2={round(centre + half)} stroke={GRID} />
      <text x={PAD.left - 6} y={PAD.top + 4} textAnchor="end" fontSize={11} fill={INK_MID}>
        +{max}
      </text>
      <text x={PAD.left - 6} y={round(centre) + 4} textAnchor="end" fontSize={11} fill={INK_MID}>
        0
      </text>
      <text x={PAD.left - 6} y={round(centre + half) + 4} textAnchor="end" fontSize={11} fill={INK_MID}>
        −{max}
      </text>
      {periods.map((period, index) => {
        const left = PAD.left + slot * index + (slot - barWidth) / 2
        const above = up[index] ?? null
        const below = down[index] ?? null
        return (
          <g key={period} onPointerEnter={() => setHover(index)}>
            <rect x={round(left - 2)} y={PAD.top} width={round(barWidth + 4)} height={half * 2} fill="transparent" />
            {above !== null && above > 0 && <rect x={round(left)} y={round(centre - scale(above))} width={round(barWidth)} height={round(scale(above))} fill={GOOD} rx={1} />}
            {below !== null && below > 0 && <rect x={round(left)} y={round(centre + 1)} width={round(barWidth)} height={round(scale(below))} fill={BAD} rx={1} />}
            {above !== null && above > 0 && (
              <text x={round(left + barWidth / 2)} y={round(centre - scale(above)) - 3} textAnchor="middle" fontSize={10} fontWeight={700} fill={INK}>
                +{above}
              </text>
            )}
            <text x={round(left + barWidth / 2)} y={height - 6} textAnchor="middle" fontSize={11} fill={INK_MID}>
              {periods.length <= 13 || index % Math.ceil(periods.length / 12) === 0 ? periodLabel(period) : ''}
            </text>
          </g>
        )
      })}
      {hover !== null && periods[hover] && (
        <foreignObject x={hover < periods.length / 2 ? round(PAD.left + slot * hover + slot) : round(PAD.left + slot * hover - 170)} y={PAD.top} width={166} height={66} style={{ overflow: 'visible' }}>
          <div style={{ background: INK, color: '#fff', borderRadius: 6, padding: '6px 10px', fontSize: 11, lineHeight: '18px' }}>
            <div style={{ color: '#B8D4DC' }}>{periodLabel(periods[hover])}</div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>{upName}</span>
              <strong>{up[hover] ?? '—'}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>{downName}</span>
              <strong>{down[hover] ?? '—'}</strong>
            </div>
          </div>
        </foreignObject>
      )}
    </svg>
  )
}
